import { useQuery } from "@apollo/client";
import { Suspense } from "react";
import { useIntl } from "react-intl";

import Suspender from "lib/Suspender";
import { WorkspaceFinancialsDoc } from "operations";

import { LoadingSkeleton } from "@joor/design-system";
import { asMaybeNumber } from "lib/conversions";
import { StyledContent, StyledLabel, StyledTitle } from "./styledComponents";

export const totalUnits = (mappedUnits: any): number[] => {
  const merge = mappedUnits?.reduce((acc: any, value: any) => {
    if (!acc[value.currency]) {
      acc[value.currency] = asMaybeNumber(value.unit) ?? 0;
    } else {
      acc[value.currency] += asMaybeNumber(value.unit) ?? 0;
    }
    return acc;
  }, {});
  return merge ? Object.values(merge) : [];
};

const useProductCount = (workspaceId: string, isBrand: boolean) => {
  const { data, loading } = useQuery(WorkspaceFinancialsDoc, {
    variables: { workspaceId },
  });

  const mappedUnits = data?.retailWorkspace?.assortments?.flatMap((a) =>
    a.financialOverview.financialDetails
      ?.filter((c) =>
        isBrand ? c.isBrandCurrency === true : c.isBrandCurrency === false
      )
      .map((c) => ({ currency: c.currencyCode, unit: c.units.productCount }))
  );
  const products = totalUnits(mappedUnits);

  return { loading, products };
};

export const ProductCount = ({
  workspaceId,
  moreCurrencies,
  isBrand,
}: {
  workspaceId: string;
  moreCurrencies: boolean;
  isBrand: boolean;
}) => {
  const intl = useIntl();
  const { loading, products } = useProductCount(workspaceId, isBrand);

  const listProductsCount = products?.map((count) => (
    <StyledTitle data-testid="product-count-value">
      {intl.formatNumber(count) ?? 0}
    </StyledTitle>
  ));

  return (
    <StyledContent>
      <StyledLabel>Products</StyledLabel>
      <Suspense fallback={<LoadingSkeleton />}>
        <Suspender suspend={loading}>
          {moreCurrencies ? (
            listProductsCount
          ) : (
            <StyledTitle data-testid="product-count-value">
              {products[0] ? intl.formatNumber(products[0]) : 0}
            </StyledTitle>
          )}
        </Suspender>
      </Suspense>
    </StyledContent>
  );
};
